import React, { useEffect, useState } from 'react';
import { Alert, Button, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { CashOrder, CashOrderItem, CashOrderSnapshot } from '../../models/cash-order-models';
import {
    getCashOrderPdf,
    useGetCashOrder as getCashOrder,
    useGetCashOrders as getCashOrders,
} from '../../utils/clients-hooks';

interface CashOrdersTableProps {
    clientId: number;
    branchId?: number;
}

const getString = (snapshot: CashOrderSnapshot | undefined, keys: string[]) => {
    for (const key of keys) {
        const value = snapshot?.[key];
        if (typeof value === 'string' && value.trim()) return value;
    }
    return undefined;
};

const getDistributor = (snapshot: CashOrderSnapshot) =>
    getString(snapshot, ['name', 'fullName', 'assignedDistributorName', 'username']) ?? '-';

const getSnapshotItems = (snapshot: CashOrderSnapshot): CashOrderSnapshot[] => {
    for (const key of ['devices', 'items']) {
        const value = snapshot[key];
        if (Array.isArray(value)) return value.filter((item): item is CashOrderSnapshot => Boolean(item) && typeof item === 'object');
    }
    return [];
};

const getDeviceModel = (order: CashOrder, item: CashOrderItem) => {
    const directModel = item.model ?? item.deviceModel ?? getString(item.passportSnapshot, ['model', 'fullModelName', 'name']);
    if (directModel) return directModel;

    const snapshotItem = getSnapshotItems(order.partySnapshot).find((candidate) =>
        [candidate.deviceId, candidate.id].some((id) => Number(id) === item.deviceId)
    );
    const passport = snapshotItem?.passportSnapshot;
    return getString(snapshotItem, ['model', 'deviceModel', 'fullModelName']) ??
        (passport && typeof passport === 'object' && !Array.isArray(passport)
            ? getString(passport as CashOrderSnapshot, ['model', 'fullModelName', 'name'])
            : undefined);
};

const getDevices = (order: CashOrder) => order.items.map((item) => {
    const model = getDeviceModel(order, item);
    return model ? `${item.deviceId}/${model}` : String(item.deviceId);
}).join(', ');

export const CashOrdersTable = ({ clientId, branchId }: CashOrdersTableProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'newRequest.cashOrders' });
    const [orders, setOrders] = useState<CashOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [printingId, setPrintingId] = useState<number>();

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(false);

        getCashOrders(clientId, branchId)
            .then((summaries) => Promise.all(summaries.map(({ id }) => getCashOrder(id))))
            .then((details) => {
                if (active) setOrders(details);
            })
            .catch(() => {
                if (active) setError(true);
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [clientId, branchId]);

    const print = async (id: number) => {
        const printWindow = window.open('', '_blank');
        setPrintingId(id);
        try {
            const pdf = await getCashOrderPdf(id);
            const url = URL.createObjectURL(pdf);
            if (printWindow) printWindow.location.href = url;
            window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch {
            printWindow?.close();
            setError(true);
        } finally {
            setPrintingId(undefined);
        }
    };

    return (
        <section className='mt-4 pt-3 border-top w-100' aria-label={t('title')}>
            <h5 className='mb-3'>{t('title')}</h5>
            {loading ? (
                <div className='d-flex align-items-center gap-2 py-3'>
                    <Spinner animation='border' size='sm' role='status' />
                    <span>{t('loading')}</span>
                </div>
            ) : error ? (
                <Alert variant='danger'>{t('loadError')}</Alert>
            ) : orders.length === 0 ? (
                <div className='text-muted py-3'>{t('empty')}</div>
            ) : (
                <div className='table-responsive cash-orders-scroll' tabIndex={0} role='region' aria-label={t('title')}>
                    <Table bordered hover size='sm' className='mb-0'>
                        <thead>
                            <tr>
                                <th>{t('number')}</th>
                                <th>{t('date')}</th>
                                <th>{t('type')}</th>
                                <th>{t('devices')}</th>
                                <th>{t('amount')}</th>
                                <th>{t('distributor')}</th>
                                <th>{t('status')}</th>
                                <th>{t('actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((order) => (
                                <tr key={order.id}>
                                    <td>{order.documentNumber}</td>
                                    <td>{formatCashOrderDate(order.issuedAt)}</td>
                                    <td>{t(`types.${order.type}`)}</td>
                                    <td>{getDevices(order) || '-'}</td>
                                    <td className='text-nowrap'>{(order.totalAmountMinor / 100).toFixed(2)} {order.currency}</td>
                                    <td>{getDistributor(order.distributorSnapshot)}</td>
                                    <td>{t(`statuses.${order.status}`)}</td>
                                    <td>
                                        <Button size='sm' onClick={() => print(order.id)} disabled={printingId === order.id}>
                                            {printingId === order.id ? <Spinner animation='border' size='sm' /> : t('print')}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}
        </section>
    );
};

export const formatCashOrderDate = (issuedAt: string) => {
    const parts = new Intl.DateTimeFormat('bg-BG', {
        timeZone: 'Europe/Sofia',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).formatToParts(new Date(issuedAt));
    return ['day', 'month', 'year'].map(type => parts.find(part => part.type === type)?.value).join('.');
};
